import { useState, useEffect } from 'react';
import { Outlet, useNavigate, NavLink } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    LayoutDashboard,
    FileText,
    Settings,
    Users,
    Stethoscope,
    DollarSign,
    LogOut,
    Menu,
    X,
    ChevronDown,
    Package,
    IndianRupee,
    BedDouble,
    Layers,
    ClipboardList,
    Activity,
} from 'lucide-react';
import { authApi } from '@/api/client';
import type { User } from '@medical-counsellor/shared';

export function AuthLayout() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Get user from localStorage
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    const user: User | null = storedUser ? JSON.parse(storedUser) : null;

    // Verify token is still valid - only run if token exists
    const { isError } = useQuery({
        queryKey: ['me'],
        queryFn: authApi.me,
        retry: false,
        enabled: !!token,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

    // Redirect to login if no token or token is invalid
    useEffect(() => {
        if (!token || isError) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            navigate('/login', { replace: true });
        }
    }, [token, isError, navigate]);

    if (!token) return null;

    const handleLogout = async () => {
        try {
            await authApi.logout();
        } catch {
            // Ignore logout errors
        }
        queryClient.clear();
        navigate('/login', { replace: true });
    };

    const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

    const navigation = [
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
        { name: 'Estimates', href: '/estimates', icon: FileText },
    ];

    const adminNavigation = [
        { name: 'Procedures', href: '/admin/procedures', icon: ClipboardList },
        { name: 'Cost Config', href: '/admin/cost-config', icon: Layers },
        { name: 'Pricing', href: '/admin/pricing', icon: IndianRupee },
        { name: 'Room Types', href: '/admin/room-types', icon: BedDouble },
        { name: 'ICU Rates', href: '/admin/icu-rates', icon: Activity },
        { name: 'Doctors', href: '/admin/doctors', icon: Stethoscope },
        { name: 'Users', href: '/admin/users', icon: Users },
        { name: 'Settings', href: '/admin/settings', icon: Settings },
    ];

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Mobile sidebar backdrop */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white
          transform transition-transform duration-200 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
            >
                {/* Logo */}
                <div className="flex items-center justify-between h-16 px-4 border-b border-slate-700">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center">
                            <Stethoscope className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-semibold text-lg">MedCounsellor</span>
                    </div>
                    <button
                        className="lg:hidden p-1 hover:bg-slate-800 rounded"
                        onClick={() => setSidebarOpen(false)}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Navigation */}
                <nav className="flex-1 px-3 py-4 space-y-1">
                    {navigation.map((item) => (
                        <NavLink
                            key={item.name}
                            to={item.href}
                            className={({ isActive }) =>
                                isActive ? 'sidebar-link-active' : 'sidebar-link'
                            }
                            onClick={() => setSidebarOpen(false)}
                        >
                            <item.icon className="w-5 h-5" />
                            {item.name}
                        </NavLink>
                    ))}

                    {isAdmin && (
                        <>
                            <div className="pt-4 pb-2">
                                <p className="px-4 text-xs font-semibold uppercase tracking-wider text-slate-400">
                                    Administration
                                </p>
                            </div>
                            {adminNavigation.map((item) => (
                                <NavLink
                                    key={item.name}
                                    to={item.href}
                                    className={({ isActive }) =>
                                        isActive ? 'sidebar-link-active' : 'sidebar-link'
                                    }
                                    onClick={() => setSidebarOpen(false)}
                                >
                                    <item.icon className="w-5 h-5" />
                                    {item.name}
                                </NavLink>
                            ))}
                        </>
                    )}
                </nav>

                {/* User info */}
                <div className="p-4 border-t border-slate-700">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium">
                                {user?.fullName?.charAt(0).toUpperCase() || 'U'}
                            </span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{user?.fullName}</p>
                            <p className="text-xs text-slate-400 capitalize">{user?.role?.replace('_', ' ')}</p>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                            title="Logout"
                        >
                            <LogOut className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <div className="lg:pl-64">
                {/* Top bar */}
                <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200 flex items-center px-4 lg:px-6">
                    <button
                        className="lg:hidden p-2 -ml-2 hover:bg-slate-100 rounded-lg"
                        onClick={() => setSidebarOpen(true)}
                    >
                        <Menu className="w-5 h-5" />
                    </button>

                    <div className="flex-1" />
                </header>

                {/* Page content */}
                <main className="p-4 lg:p-6">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
